import {
  DEFAULT_LANGUAGE_CODE,
  getUiLanguageCatalog,
  normalizeUiLanguageCode,
} from "./languages.js";
import rawPatterns from "./i18n-copy-patterns.json" with { type: "json" };
import rawTranslations from "./i18n-copy-translations.json" with { type: "json" };

export const UI_COPY_TRANSLATIONS = Object.freeze(rawTranslations);

function buildCopyPattern(pattern) {
  return Object.freeze({
    sourcePattern: new RegExp(pattern.sourcePattern.source, pattern.sourcePattern.flags),
    translations: Object.freeze(pattern.translations || {}),
    ...(Array.isArray(pattern.preserve) ? { preserve: Object.freeze([...pattern.preserve]) } : {}),
  });
}

export const UI_COPY_PATTERNS = Object.freeze(rawPatterns.map(buildCopyPattern));

export function normalizeUiCopySource(value = "") {
  return String(value || "").replace(/\s+/g, " ").trim();
}

function preserveBoundaryWhitespace(originalValue, translatedValue) {
  const original = String(originalValue || "");
  const leading = original.match(/^\s*/)?.[0] || "";
  const trailing = original.match(/\s*$/)?.[0] || "";
  return `${leading}${translatedValue}${trailing}`;
}

function translateUiCopyByPattern(value = "", locale = DEFAULT_LANGUAGE_CODE) {
  const source = normalizeUiCopySource(value);
  for (const pattern of UI_COPY_PATTERNS) {
    const match = source.match(pattern.sourcePattern);
    const template = pattern.translations?.[locale];
    if (!match || !template) {
      continue;
    }
    const groups = match.groups || {};
    const translated = template.replace(/\{([a-zA-Z0-9_]+)\}/g, (_, name) => {
      const rawValue = groups[name] || "";
      return pattern.preserve?.includes(name) ? rawValue : translateUiCopy(rawValue, locale) || rawValue;
    });
    return preserveBoundaryWhitespace(value, translated);
  }
  return "";
}

export function translateUiCopy(value = "", locale = DEFAULT_LANGUAGE_CODE) {
  const normalizedLocale = normalizeUiLanguageCode(locale);
  const source = normalizeUiCopySource(value);
  return translateUiCopyFromSource(value, source, normalizedLocale) || translateUiCopyByPattern(value, normalizedLocale);
}

function translateUiCopyFromSource(value = "", source = "", locale = DEFAULT_LANGUAGE_CODE) {
  if (!source) {
    return "";
  }
  if (locale === DEFAULT_LANGUAGE_CODE) {
    return preserveBoundaryWhitespace(value, source);
  }
  const translated = UI_COPY_TRANSLATIONS[source]?.[locale];
  if (!translated) {
    return "";
  }
  return preserveBoundaryWhitespace(value, translated);
}

export function getStaticUiCopySupportedLocales() {
  return getUiLanguageCatalog()
    .map((language) => language.code)
    .filter((code) => code !== DEFAULT_LANGUAGE_CODE);
}
